import { Glasses, Key, Mail } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { Text, XStack, YStack } from "tamagui";
import { AccountSheetActive } from "../../../plans/PlanModel";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { Sheet, type SheetProps } from "../../../ui/Sheet";
import { Title } from "../../../ui/Title";
import { UserCircle } from "../../../ui/UserCircle";
import { LoginSheets } from "../../account/LoginSheets";
import { useStores } from "../../stores";
import { isRunningInApp } from "../../utils";
import { CreateWizardButton } from "./CreateWizardButton";
import { CreateWizardProgress } from "./CreateWizardProgress";
import { type CreateWizardState, CreateWizardStep } from "./CreateWizardState";
import { Selection } from "./Selection";

const DisplayNameSheet: React.FC<
  { state: CreateWizardState } & Omit<SheetProps, "sheetId">
> = observer(({ state, ...props }) => {
  const goToNextStep = useCallback(() => {
    state.setActiveStepId(CreateWizardStep.DATE);
  }, [state]);

  return (
    <Sheet
      sheetId="displayNameSheet"
      header={<Title>Who are you?</Title>}
      footer={
        <Button onPress={goToNextStep} disabled={!state?.displayName}>
          Continue
        </Button>
      }
      {...props}
    >
      <YStack gap="$medium">
        <Body>
          This is what other members will see you as. It can be your real name
          or an alias, but the members you invite should be able to identify you
          by it.
        </Body>

        <XStack alignItems="center" gap="$medium">
          <UserCircle name={state.displayName ?? ""} size={42} />
          <Input
            type="border"
            placeholder="Your name or alias..."
            onChangeText={(text) => state.setDisplayName(text)}
            preventNewLine
            submitOnEnter
            defaultValue={state.displayName}
            onSubmitEditing={(e) => {
              if (e.nativeEvent.text) {
                state.setDisplayName(e.nativeEvent.text);

                goToNextStep();
              }
            }}
          />
        </XStack>
      </YStack>
    </Sheet>
  );
});

export const WizardStepUserLoggedIn: React.FC<{ state: CreateWizardState }> =
  observer(({ state }) => {
    return (
      <>
        <Box backgroundColor={"$primary"} gap={"$small"}>
          <Title>What should we call you?</Title>
          <Text>
            This is the name other members will see for you in this Planira. Use
            your real name or pick something unique — it's totally up to you, as
            long as it helps others recognize you!
          </Text>

          <CreateWizardProgress state={state} />
        </Box>

        <Box>
          <Label>Name or alias</Label>

          <XStack alignItems="center" gap="$medium">
            <UserCircle name={state.displayName ?? ""} size={42} />
            <Input
              type="border"
              placeholder="Your name or alias..."
              onChangeText={(text) => state.setDisplayName(text)}
              preventNewLine
              submitOnEnter
              defaultValue={state.displayName}
              onSubmitEditing={(e) => {
                if (e.nativeEvent.text) {
                  state.setDisplayName(e.nativeEvent.text);

                  state.setActiveStepId(CreateWizardStep.DATE);
                }
              }}
            />
          </XStack>
        </Box>
        <CreateWizardButton
          disabled={!state?.displayName}
          onPress={() => {
            state.setActiveStepId(CreateWizardStep.DATE);
          }}
          position={"sticky" as "static"}
          bottom={0}
          subText={
            state.displayName
              ? `What a fancy name you got, ${state.displayName}!`
              : "What should we call you?"
          }
        >
          Continue
        </CreateWizardButton>
      </>
    );
  });

export const WizardStepUserLoggedOut: React.FC<{ state: CreateWizardState }> =
  observer(({ state }) => {
    const [showLoginSheet, setShowLoginSheet] = React.useState(
      AccountSheetActive.None,
    );
    const [showDisplayNameSheet, setShowDisplayNameSheet] =
      React.useState(false);

    const gotoNextStep = React.useCallback(() => {
      // Will be flipped to registred user content
      return;
      // state.setActiveStepId(CreateWizardStep.DATE);
    }, []);

    return (
      <>
        <Box backgroundColor={"$primary"} gap={"$small"}>
          <Title>Create user</Title>
          <Text>Continue anonymously or sign up to have more control</Text>

          <CreateWizardProgress state={state} />
        </Box>

        <Selection
          onSelect={() => setShowLoginSheet(AccountSheetActive.Register)}
          title={"Continue with email"}
          description="Create an account to easily access your Planiras without having access
          to the share link in the future"
          icon={Mail}
        />

        <Selection
          onSelect={() => setShowDisplayNameSheet(true)}
          title={"Continue anonymously"}
          description="Continue without creating an account and have the Planira remembered
          locally on your device and access through the share link"
          icon={Glasses}
        />

        <Selection
          onSelect={() => setShowLoginSheet(AccountSheetActive.Login)}
          title={"Sign in"}
          description="If you already have an account, sign in and get started directly"
          icon={Key}
        />

        <LoginSheets
          currentSheet={showLoginSheet}
          onChangeSheet={setShowLoginSheet}
          onCurrentSheetDone={gotoNextStep}
        />

        <DisplayNameSheet
          state={state}
          open={showDisplayNameSheet}
          onOpenChange={(open: boolean) => setShowDisplayNameSheet(open)}
        />
      </>
    );
  });

export const WizardStepUser: React.FC<{ state: CreateWizardState }> = observer(
  ({ state }) => {
    const { sessionStore } = useStores();

    return (
      <YStack gap="$medium">
        {sessionStore.isRegistered || isRunningInApp() ? (
          <WizardStepUserLoggedIn state={state} />
        ) : (
          <WizardStepUserLoggedOut state={state} />
        )}
      </YStack>
    );
  },
);
